import { captureRetainedAudioOwner, retainedAudioOwnerIsCurrent, retainDeliveredBriefAudio, retainDeliveredNewsAudio } from "./retained-audio-store";
import { invalidateRetainedAudioPauseCheckpoint, bindRetainedAudioPauseCheckpoint } from "./retained-audio-resume";
import { installFullBriefAudioCompletion } from "./full-brief-completion";
import { acceptedBriefAudioShareExcerpts, acceptedBriefAudioStories, type BriefAudioCompletionContext } from "./brief-audio-completion-store";
import { installRetainedAudioRuntime } from "./retained-audio-playback";
export { playRetainedAudio, playRetainedBriefRecapSection } from "./retained-audio-playback";
import { installStandardBriefPreviewRuntime } from "./standard-brief-preview";
export { playStandardBriefPreview, standardBriefPreviewAudioKey } from "./standard-brief-preview";
import { enqueueAudioRequest, normalizeQueueEntry, recordAudioHistory, recordAudioPlaybackStarted, removeAudioQueueEntry } from "./audio-library-metadata";
export { getAudioHistory, getAudioQueue, removeAudioQueueEntry, clearAudioHistory, clearAudioQueue, onAudioLibraryChanged } from "./audio-library-metadata";
import type { ActivitySource } from "@/shared/api/activity-source";
import { authFetch, getWebDeviceId } from "@/shared/auth/session";
import { ApiError, jsonFromResponse, normalizeApiError } from "@/shared/api/client";
import { trackPublicWebEvent } from "@/shared/analytics/analytics";
import { publicWebCopyForLang } from "@/shared/i18n/public-web-copy";
import { loadPreferences } from "@/shared/preferences/store";
import { createAudioRequestOwnership } from "./audio-request-ownership.mjs";
import { defaultDownloadName, sanitizeDownloadName } from "./audio-download-name.mjs";
import {
  beginDashboardAudioPlayAttempt,
  clearDashboardAudioPlayAttempt,
  completeDashboardAudioPlayAttempt,
} from "./dashboard-audio-play-attempt.mjs";
import {
  assertDashboardBriefingV2Attested,
  buildDashboardBriefingV2Payload,
  normalizeDashboardBriefingV2Payload,
} from "./dashboard-briefing-v2.mjs";
import { createSilentWavDataUri } from "./ios-audio-primer.mjs";
import { createBriefStartAudioPrimer } from "./brief-start-audio-primer.mjs";
import {
  briefJourneyRequestBody,
  briefJourneySectionAudioKey,
  cancelBriefJourneyAudioPreparation,
  getPreparedBriefJourneyAudio,
  prepareBriefJourneyAudioSections,
  rememberPreparedBriefJourneyMp3,
} from "./brief-journey-audio-preparation";
import {
  contentAudioKey,
  dashboardBriefingAudioKey,
  newsCategoryAudioKey,
  newsStoryAudioKey,
  stockWhyAudioKey,
} from "./audio-playback-keys";
import {
  bufferedFraction,
  currentAudioTimeOfDay,
  decodeMp3,
  finiteDuration,
  localDateKey,
  seekMediaPlaybackTo,
  setMediaPlaybackRatePreservingPosition,
  stringValue,
} from "./audio-media-utils.mjs";
import { canonicalAudioArticle } from "./audio-payload-parity.mjs";
import { audioMessageKeyForError, audioQuotaKindForError, audioSubscribeCtaAuthorityForError } from "./audio-error-policy.mjs";
import { resolveCanonicalAudioResponse } from "./audio-response-contract.mjs";
import {
  queuedAudioEntryIsCurrentMarketDay,
} from "./audio-queue-policy";
import {
  assertAudioVoiceIdSupportedForLanguage,
  normalizedAudioVoiceId,
} from "./audio-voice-policy.mjs";
import { dashboardDeviceSpeech } from "./dashboard-device-speech";
import {
  captureDailyDefaultEditionAudioOffer,
  clearDailyDefaultEditionAudioContext,
  installDailyDefaultEditionAudioRuntime,
} from "./daily-default-edition-audio";
import { negotiateDeviceSpeechForAudioRequest } from "./device-speech-negotiation.mjs";
import type { AudioPlaybackState } from "./audio-playback-state";
export type { AudioPlaybackPhase, AudioPlaybackState, AudioQuotaKind } from "./audio-playback-state";
export { shouldShowAudioUpgradeCta } from "./audio-error-policy.mjs";
import type {
  AudioArticleInput,
  AudioQueueEntry,
  AudioResponse,
  BriefJourneySectionAudioOpts,
} from "./audio-types";
export type {
  AudioArticleInput,
  AudioHistoryEntry,
  AudioQueueEntry,
  BriefJourneySectionAudioOpts,
  BriefNarrationMatchInput,
  BriefNarrationTaskInput,
} from "./audio-types";
export {
  contentAudioKey,
  dashboardBriefingAudioKey,
  newsCategoryAudioKey,
  newsStoryAudioKey,
  stockWhyAudioKey,
} from "./audio-playback-keys";
export type { ContentAudioKind } from "./audio-playback-keys";

let audioEl: HTMLAudioElement | null = null;
let objectUrl: string | null = null;
let playbackRate = 1;
type ActiveAudioKind = "direct" | "brief-journey";
let activeAudioKind: ActiveAudioKind = "direct";
const audioEventCleanups = new WeakMap<HTMLAudioElement, () => void>();
const audioRequestOwnership = createAudioRequestOwnership();
const playbackEvents = new EventTarget();
const playbackTimelineEvents = new EventTarget();
const playbackEndedListeners = new Set<(key: string) => void>();
const SILENT_WAV_DATA_URI = createSilentWavDataUri();
export {
  briefJourneySectionAudioKey,
  cancelBriefJourneyAudioPreparation,
  prepareBriefJourneyAudioSections,
};
export const AUDIO_PLAYBACK_SPEED_OPTIONS = [0.75, 1, 1.25, 1.5, 2, 3] as const;
export function getAudioPlaybackRate(): number {
  return playbackRate;
}
export function setAudioPlaybackRate(value: number): number {
  playbackRate =
    typeof value === "number" && Number.isFinite(value) ? Math.min(3, Math.max(0.75, value)) : 1;
  dashboardDeviceSpeech.setRate(playbackRate);
  if (audioEl) setMediaPlaybackRatePreservingPosition(audioEl, playbackRate);
  if (
    !playbackState.deviceSpeech &&
    (playbackState.phase === "loading" || playbackState.phase === "playing")
  ) {
    setPlaybackState({ ...playbackState, playbackRate });
  }
  updateMediaSessionPosition();
  return playbackRate;
}

let playbackState: AudioPlaybackState = { phase: "idle" };

function setPlaybackState(next: AudioPlaybackState) {
  playbackState = next;
  playbackEvents.dispatchEvent(new Event("change"));
}

function setPlaybackTimelineState(next: AudioPlaybackState) {
  playbackState = next;
  playbackTimelineEvents.dispatchEvent(new Event("change"));
}
export function getAudioPlaybackState(): AudioPlaybackState {
  return playbackState;
}
export function onAudioPlaybackChanged(listener: () => void): () => void {
  playbackEvents.addEventListener("change", listener);
  return () => playbackEvents.removeEventListener("change", listener);
}
export function onAudioPlaybackTimelineChanged(listener: () => void): () => void {
  playbackTimelineEvents.addEventListener("change", listener);
  return () => playbackTimelineEvents.removeEventListener("change", listener);
}
export function onAudioPlaybackEnded(listener: (key: string) => void): () => void {
  playbackEndedListeners.add(listener);
  return () => playbackEndedListeners.delete(listener);
}

function revokeObjectUrl() {
  if (objectUrl) {
    URL.revokeObjectURL(objectUrl);
    objectUrl = null;
  }
}


function resetMediaSession() {
  if ("mediaSession" in navigator) {
    try {
      navigator.mediaSession.metadata = null;
      navigator.mediaSession.playbackState = "none";
    } catch {
      /* ignore */
    }
  }
}

function setMediaSessionPlaybackState(state: "none" | "paused" | "playing") {
  if (!("mediaSession" in navigator)) return;
  try {
    navigator.mediaSession.playbackState = state;
  } catch {
    /* ignore */
  }
}

function setMediaSession(label: string, detail?: string, lang?: string) {
  if (!("mediaSession" in navigator) || typeof MediaMetadata === "undefined") return;
  const copy = audioCopy(lang);
  try {
    navigator.mediaSession.metadata = new MediaMetadata({
      title: label,
      artist: "Today's Brief",
      album: detail || copy("audio.mediaAlbumFallback"),
    });
    navigator.mediaSession.playbackState = "playing";
    navigator.mediaSession.setActionHandler("pause", () => pauseAudioPlayback());
    navigator.mediaSession.setActionHandler("play", () => {
      void resumeAudioPlayback();
    });
    navigator.mediaSession.setActionHandler("stop", () => stopAudioPlayback());
    navigator.mediaSession.setActionHandler("seekbackward", (event) => {
      seekAudioPlaybackBy(-(event.seekOffset || 15));
    });
    navigator.mediaSession.setActionHandler("seekforward", (event) => {
      seekAudioPlaybackBy(event.seekOffset || 15);
    });
    navigator.mediaSession.setActionHandler("seekto", (event) => {
      if (typeof event.seekTime === "number") seekAudioPlaybackTo(event.seekTime);
    });
  } catch {
    /* ignore */
  }
}

function detachAudioEvents(player: HTMLAudioElement) {
  audioEventCleanups.get(player)?.();
  audioEventCleanups.delete(player);
}

function clearAudioElement(resetBriefJourneyAuthorization = false) {
  if (resetBriefJourneyAuthorization) dashboardDeviceSpeech.resetBriefJourneyAuthorization();
  dashboardDeviceSpeech.discard();
  if (audioEl) {
    const player = audioEl;
    audioEl = null;
    detachAudioEvents(player);
    try {
      player.pause();
      player.removeAttribute("src");
      player.load();
    } catch {
      /* ignore */
    }
  }
  revokeObjectUrl();
  activeAudioKind = "direct";
  resetMediaSession();
}
export function stopAudioPlayback() {
  clearDailyDefaultEditionAudioContext();
  audioRequestOwnership.cancel();
  clearDashboardAudioPlayAttempt();
  clearAudioElement(true);
  setPlaybackState({ phase: "idle" });
}
export function pauseAudioPlayback(): boolean {
  if (playbackState.phase !== "playing" || playbackState.paused) return false;
  if (playbackState.deviceSpeech) {
    if (!dashboardDeviceSpeech.pause()) return false;
    setPlaybackState({ ...playbackState, paused: true, buffering: false });
    setMediaSessionPlaybackState("paused");
    return true;
  }
  if (!audioEl) return false;
  audioEl.pause();
  setPlaybackState({ ...playbackState, paused: true, buffering: false });
  setMediaSessionPlaybackState("paused");
  return true;
}
export async function resumeAudioPlayback(): Promise<boolean> {
  if (playbackState.phase !== "playing" || !playbackState.paused) return false;
  if (playbackState.deviceSpeech) {
    if (!dashboardDeviceSpeech.resume()) return false;
    setPlaybackState({ ...playbackState, paused: false, buffering: false });
    setMediaSessionPlaybackState("playing");
    return true;
  }
  if (!audioEl) return false;
  const player = audioEl;
  const key = playbackState.key;
  const label = playbackState.label;
  const lang = loadPreferences().lang;
  try {
    await player.play();
    if (audioEl !== player || playbackState.key !== key) return false;
    setPlaybackState({ ...playbackState, paused: false, buffering: false });
    setMediaSessionPlaybackState("playing");
    return true;
  } catch {
    if (audioEl !== player || playbackState.key !== key) return false;
    clearAudioElement();
    setPlaybackState({
      phase: "error",
      key,
      label,
      detail: audioCopy(lang)("audio.error.browserPlayback"),
      code: "browser_audio_resume_error",
    });
    return false;
  }
}

export function seekAudioPlaybackTo(seconds: number): boolean {
  if (playbackState.phase !== "playing" || !Number.isFinite(seconds)) return false;
  if (playbackState.deviceSpeech) return dashboardDeviceSpeech.seekToPositionMs(seconds * 1000);
  if (!audioEl) return false;
  if (!seekMediaPlaybackTo(audioEl, seconds)) return false;
  syncPlaybackTimeline(audioEl);
  return true;
}

export function seekAudioPlaybackBy(deltaSeconds: number): boolean {
  if (!Number.isFinite(deltaSeconds)) return false;
  const positionSeconds = playbackState.deviceSpeech
    ? (playbackState.positionMs || 0) / 1000
    : audioEl?.currentTime;
  return typeof positionSeconds === "number"
    ? seekAudioPlaybackTo(positionSeconds + deltaSeconds)
    : false;
}
export function seekAudioPlaybackToFraction(fraction: number): boolean {
  if (!Number.isFinite(fraction)) return false;
  const duration = playbackState.deviceSpeech
    ? (playbackState.durationMs || 0) / 1000
    : audioEl
      ? finiteDuration(audioEl)
      : 0;
  if (duration <= 0) return false;
  return seekAudioPlaybackTo(duration * Math.min(1, Math.max(0, fraction)));
}

export const stopBriefingPlayback = stopAudioPlayback;
export { currentAudioTimeOfDay };
function requestedAudioVoiceId(explicitVoiceId: unknown): string {
  return normalizedAudioVoiceId(explicitVoiceId || loadPreferences().audioVoiceId);
}
function audioCopy(lang?: string) {
  return publicWebCopyForLang(lang || loadPreferences().lang);
}
function emitAudioError(
  err: unknown,
  key: string,
  label: string,
  lang?: string,
  dailyDefaultAudioOffer?: AudioPlaybackState["dailyDefaultAudioOffer"],
): AudioPlaybackState {
  const error = normalizeApiError(err, "audio_unavailable");
  const next: AudioPlaybackState = {
    phase: "error",
    key,
    label,
    detail: audioCopy(lang)(audioMessageKeyForError(error)),
    code: error.code,
    httpStatus: error.status,
    quotaKind: audioQuotaKindForError(error),
    showSubscribeCta: audioSubscribeCtaAuthorityForError(error),
    dailyDefaultAudioOffer,
  };
  setPlaybackState(next);
  return next;
}

export function retryDeviceSpeechPlayback(): boolean {
  return dashboardDeviceSpeech.playPrepared();
}
function createPrimedAudioElement(kind: ActiveAudioKind = "direct"): HTMLAudioElement {
  invalidateRetainedAudioPauseCheckpoint();
  clearAudioElement(kind !== "brief-journey");
  const player = new Audio(SILENT_WAV_DATA_URI);
  player.playbackRate = playbackRate;
  player.loop = true;
  player.preload = "auto";
  audioEl = player;
  activeAudioKind = kind;
  void player.play().catch(() => { /* Actual content playback owns any authoritative failure. */ });
  return player;
}
function acquireBriefJourneyAudioElement(): HTMLAudioElement {
  if (audioEl && activeAudioKind === "brief-journey") {
    const player = audioEl;
    detachAudioEvents(player);
    revokeObjectUrl();
    try {
      player.loop = true;
      player.src = SILENT_WAV_DATA_URI;
      player.load();
      player.playbackRate = playbackRate;
      void player.play().catch(() => {
        /* Actual content playback owns any authoritative failure. */
      });
    } catch { /* Actual content playback owns any authoritative failure. */ }
    return player;
  }
  return createPrimedAudioElement("brief-journey");
}
const briefStartPrimer = createBriefStartAudioPrimer({
  prime: () => { audioRequestOwnership.cancel(); dashboardDeviceSpeech.discard(); return createPrimedAudioElement(); },
  isCurrent: (player: HTMLAudioElement) => audioEl === player,
  discard: (player: HTMLAudioElement) => clearDetachedAudioElement(player, null),
});
export const primeDashboardBriefStartAudio = () => briefStartPrimer.prime();
export function primeBriefJourneyAudioPlayback() {
  clearDailyDefaultEditionAudioContext();
  audioRequestOwnership.cancel();
  dashboardDeviceSpeech.resetBriefJourneyAuthorization();
  createPrimedAudioElement("brief-journey");
  setPlaybackState({ phase: "idle" });
}
export function stopBriefJourneyAudioPlayback() {
  dashboardDeviceSpeech.resetBriefJourneyAuthorization();
  if (activeAudioKind === "brief-journey" || playbackState.key?.startsWith("brief-section:")) {
    stopAudioPlayback();
  }
}
function clearDetachedAudioElement(
  player: HTMLAudioElement,
  requestObjectUrl: string | null,
  preserveReusedActivePlayer = false,
  preserveMediaSession = false,
) {
  const wasActivePlayer = audioEl === player;
  if (wasActivePlayer && preserveReusedActivePlayer) {
    if (requestObjectUrl && objectUrl !== requestObjectUrl) URL.revokeObjectURL(requestObjectUrl);
    return;
  }
  if (wasActivePlayer) audioEl = null;
  detachAudioEvents(player);
  try {
    player.pause();
    player.removeAttribute("src");
    player.load();
  } catch {
    /* ignore */
  }
  if (requestObjectUrl) {
    if (objectUrl === requestObjectUrl) objectUrl = null;
    URL.revokeObjectURL(requestObjectUrl);
  }
  if (wasActivePlayer) {
    activeAudioKind = "direct";
    if (!preserveMediaSession) resetMediaSession();
  }
}

function updateMediaSessionPosition() {
  if (
    !audioEl ||
    playbackState.phase !== "playing" ||
    !("mediaSession" in navigator) ||
    typeof navigator.mediaSession.setPositionState !== "function"
  ) {
    return;
  }
  const duration = finiteDuration(audioEl);
  if (duration <= 0) return;
  try {
    navigator.mediaSession.setPositionState({
      duration,
      playbackRate,
      position: Math.min(duration, Math.max(0, audioEl.currentTime || 0)),
    });
  } catch {
    /* ignore browsers that expose partial Media Session support */
  }
}

function syncPlaybackTimeline(
  player: HTMLAudioElement,
  overrides: Pick<AudioPlaybackState, "buffering" | "paused"> = {},
  notifyPlaybackListeners = false,
) {
  if (audioEl !== player || player.loop || playbackState.phase !== "playing") return;
  const duration = finiteDuration(player);
  const position = Math.min(
    duration || Number.MAX_SAFE_INTEGER,
    Math.max(0, player.currentTime || 0),
  );
  setPlaybackTimelineState({
    ...playbackState,
    positionMs: Math.round(position * 1000),
    durationMs: Math.round(duration * 1000),
    buffered01: bufferedFraction(player, duration),
    playbackRate: player.playbackRate || playbackRate,
    ...overrides,
  });
  if (notifyPlaybackListeners) playbackEvents.dispatchEvent(new Event("change"));
  if (overrides.paused === true) setMediaSessionPlaybackState("paused");
  if (overrides.paused === false) setMediaSessionPlaybackState("playing");
  updateMediaSessionPosition();
}

function parkBriefJourneyAudioElement(player: HTMLAudioElement) {
  detachAudioEvents(player);
  revokeObjectUrl();
  resetMediaSession();
  try {
    player.loop = true;
    player.src = SILENT_WAV_DATA_URI;
    player.load();
    player.playbackRate = playbackRate;
    void player.play().catch(() => {
      /* The next section play remains authoritative. */
    });
  } catch {
    /* The next section play remains authoritative. */
  }
}

function attachAudioEvents(
  player: HTMLAudioElement,
  opts: {
    key: string;
    label: string;
    lang?: string;
    retainBriefJourneyPlayer?: boolean;
  },
): { armContent: (sourceUrl: string) => void } {
  detachAudioEvents(player);
  let contentSourceUrl = "";
  const onLoadedMetadata = () => syncPlaybackTimeline(player);
  const onDurationChange = () => syncPlaybackTimeline(player);
  const onTimeUpdate = () => syncPlaybackTimeline(player);
  const onProgress = () => syncPlaybackTimeline(player);
  const onSeeked = () => syncPlaybackTimeline(player, { buffering: false });
  const onWaiting = () => syncPlaybackTimeline(player, { buffering: true });
  const onPlaying = () => syncPlaybackTimeline(player, { buffering: false, paused: false }, true);
  const onPause = () => {
    if (!player.ended) syncPlaybackTimeline(player, { buffering: false, paused: true }, true);
  };
  const onEnded = () => {
    if (audioEl !== player) return;
    if (opts.retainBriefJourneyPlayer && activeAudioKind === "brief-journey") {
      parkBriefJourneyAudioElement(player);
    } else {
      clearAudioElement();
    }
    setPlaybackState({ phase: "idle" });
    playbackEndedListeners.forEach((listener) => listener(opts.key));
  };
  const onError = () => {
    if (audioEl !== player) return;
    // The silent primer is a best-effort user-activation bridge. A primer
    // decode failure must not cancel the in-flight request for real content.
    if (!contentSourceUrl || player.currentSrc !== contentSourceUrl) return;
    audioRequestOwnership.cancel();
    clearAudioElement();
    setPlaybackState({
      phase: "error",
      key: opts.key,
      label: opts.label,
      detail: audioCopy(opts.lang)("audio.error.browserPlayback"),
      code: "browser_audio_error",
    });
  };
  const listeners: Array<[keyof HTMLMediaElementEventMap, EventListener]> = [
    ["loadedmetadata", onLoadedMetadata],
    ["durationchange", onDurationChange],
    ["timeupdate", onTimeUpdate],
    ["progress", onProgress],
    ["seeked", onSeeked],
    ["waiting", onWaiting],
    ["playing", onPlaying],
    ["pause", onPause],
    ["ended", onEnded],
    ["error", onError],
  ];
  listeners.forEach(([type, listener]) => player.addEventListener(type, listener));
  audioEventCleanups.set(player, () => {
    listeners.forEach(([type, listener]) => player.removeEventListener(type, listener));
  });
  return {
    armContent(sourceUrl) {
      contentSourceUrl = sourceUrl;
    },
  };
}

function deviceSpeechHooks(
  key: string,
  label: string,
  detail: string | undefined,
  onAttemptSettled?: () => void,
) {
  return {
    getState: () => playbackState,
    setState: setPlaybackState,
    setTimelineState: setPlaybackTimelineState,
    resetMediaSession,
    setMediaSession,
    onAttemptSettled,
    onEnded: (endedKey: string) => playbackEndedListeners.forEach((listener) => listener(endedKey)),
    onStarted: (state: AudioPlaybackState) =>
      recordAudioPlaybackStarted(state, key, label, detail),
  };
}

installDailyDefaultEditionAudioRuntime({
  beginRequest: () => audioRequestOwnership.begin(), createPlayer: () => createPrimedAudioElement(),
  clearPlayer: clearDetachedAudioElement, deviceSpeechHooks, getRate: () => playbackRate,
  getState: () => playbackState, isActivePlayer: (player) => audioEl === player,
  onEnded: (key) => playbackEndedListeners.forEach((listener) => listener(key)),
  onMp3Started: recordAudioPlaybackStarted,
  replaceObjectUrl: (value) => { revokeObjectUrl(); objectUrl = value; },
  setMediaSession, setState: setPlaybackState, setTimelineState: setPlaybackTimelineState,
  stop: stopAudioPlayback, updateMediaSessionPosition,
});

installRetainedAudioRuntime({
  beginRequest: () => audioRequestOwnership.begin(), createPlayer: () => createPrimedAudioElement(),
  clearPlayer: clearDetachedAudioElement, attachEvents: attachAudioEvents,
  replaceObjectUrl: (value) => { revokeObjectUrl(); objectUrl = value; },
  setMediaSession, setState: setPlaybackState, getState: () => playbackState, getRate: () => playbackRate,
  clearEditionContext: clearDailyDefaultEditionAudioContext,
  isPlayerCurrent: (player) => audioEl === player,
});
installStandardBriefPreviewRuntime({
  beginRequest: () => audioRequestOwnership.begin(), createPlayer: () => createPrimedAudioElement(),
  clearPlayer: clearDetachedAudioElement, attachEvents: attachAudioEvents,
  replaceObjectUrl: (value) => { revokeObjectUrl(); objectUrl = value; },
  setMediaSession, setState: setPlaybackState, getState: () => playbackState, getRate: () => playbackRate,
  clearEditionContext: clearDailyDefaultEditionAudioContext,
});

async function postAudio(
  path: string,
  body: Record<string, unknown>,
  opts: {
    key: string;
    label: string;
    detail?: string;
    fallbackCode: string;
    lang?: string;
    downloadName?: string;
    onResolved?: (data: AudioResponse) => void;
    playerKind?: ActiveAudioKind;
    startDashboardDeviceSpeechAfterAuthorization?: boolean;
    activitySource: ActivitySource;
    fullBriefCompletion?: boolean;
    fullBriefContext?: BriefAudioCompletionContext;
    fullBriefStories?: AudioArticleInput[];
  },
): Promise<AudioPlaybackState> {
  clearDailyDefaultEditionAudioContext();
  const retainedOwner = captureRetainedAudioOwner();
  const playAttemptId =
    path === "/api/dashboard-briefing-audio"
      ? beginDashboardAudioPlayAttempt(
          body,
          playbackState.phase === "error" && playbackState.key === opts.key,
        )
      : undefined;
  const lang =
    opts.lang || stringValue(body.lang) || stringValue(body.languageCode) || loadPreferences().lang;
  const ownership = audioRequestOwnership.begin();
  const player =
    opts.playerKind === "brief-journey"
      ? acquireBriefJourneyAudioElement()
      : path === "/api/dashboard-briefing-audio" ? briefStartPrimer.acquire(createPrimedAudioElement) : createPrimedAudioElement();
  const requestedVoiceId = requestedAudioVoiceId(body.voiceId);
  dashboardDeviceSpeech.discard();
  let requestObjectUrl: string | null = null;
  const completion = opts.fullBriefCompletion === true && path === "/api/dashboard-briefing-audio" &&
    opts.playerKind !== "brief-journey" ? installFullBriefAudioCompletion({
      player, owner: retainedOwner, language: lang, isActive: () => audioEl === player,
      context: opts.fullBriefContext, excerpts: acceptedBriefAudioShareExcerpts(body),
      acceptedStories: acceptedBriefAudioStories(body, opts.fullBriefStories),
    }) : null;
  const audioEvents = attachAudioEvents(player, {
    ...opts,
    lang,
    retainBriefJourneyPlayer: opts.playerKind === "brief-journey",
  });
  if (completion) {
    const cleanEvents = audioEventCleanups.get(player);
    audioEventCleanups.set(player, () => { cleanEvents?.(); completion.onDetached(); });
  }
  setPlaybackState({ phase: "loading", key: opts.key, label: opts.label, detail: opts.detail });
  try {
    if (
      path === "/api/dashboard-briefing-audio" &&
      !normalizeDashboardBriefingV2Payload(body)
    ) {
      throw new ApiError({ status: 422, code: "audio_no_content" });
    }
    assertAudioVoiceIdSupportedForLanguage(requestedVoiceId, lang);
    const negotiation = await negotiateDeviceSpeechForAudioRequest({
      path,
      requestedVoiceId,
      languageCode: lang,
      countryCode: stringValue(body.country) || stringValue(body.countryCode),
      signal: ownership.signal,
    });
    if (negotiation?.availability === "required-voice-unavailable") {
      throw new ApiError({
        status: 422,
        code: "device_speech_voice_unavailable",
        detail: audioCopy(lang)("audio.error.deviceVoiceUnavailable"),
      });
    }
    const releaseDashboardPrimer =
      negotiation?.kind === "dashboard" &&
      opts.startDashboardDeviceSpeechAfterAuthorization === true
        ? () => clearDetachedAudioElement(player, requestObjectUrl, false, true)
        : undefined;
    const deviceSpeechInput: Parameters<typeof dashboardDeviceSpeech.prepare>[0] | null =
      negotiation?.availability === "available"
        ? {
            body,
            kind: negotiation.kind,
            key: opts.key,
            label: opts.label,
            detail: opts.detail,
            lang,
            rate: playbackRate,
            hooks: deviceSpeechHooks(opts.key, opts.label, opts.detail, releaseDashboardPrimer),
          }
        : null;
    const res = await authFetch(path, {
      activitySource: opts.activitySource,
      method: "POST",
      signal: ownership.signal,
      body: JSON.stringify({
        clientDeviceId: getWebDeviceId(),
        clientLocalDate: localDateKey(),
        ...(playAttemptId ? { playAttemptId } : {}),
        ...body,
        ...(deviceSpeechInput ? { clientCapabilities: { deviceSpeechFallback: true } } : {}),
        voiceId: requestedVoiceId,
      }),
    });
    const data = await jsonFromResponse<AudioResponse>(res, {
      fallbackCode: opts.fallbackCode,
    });
    if (!ownership.isCurrent()) {
      clearDetachedAudioElement(player, requestObjectUrl, true);
      return getAudioPlaybackState();
    }
    if (path === "/api/dashboard-briefing-audio") assertDashboardBriefingV2Attested(data, body);
    const resolution = resolveCanonicalAudioResponse(data, {
      requestedVoiceId,
      deviceSpeechAuthorized: deviceSpeechInput !== null,
    });
    if (resolution.kind === "device-speech") {
      completion?.dispose();
      const canonicalPlanPrepared =
        deviceSpeechInput !== null &&
        dashboardDeviceSpeech.prepare({
          ...deviceSpeechInput,
          speechText: resolution.speechText,
        });
      if (!canonicalPlanPrepared) {
        throw new ApiError({
          status: 0,
          code: "device_speech_not_prepared",
          detail: audioCopy(lang)("audio.error.browserPlayback"),
        });
      }
      const startDashboardDeviceSpeech =
        opts.startDashboardDeviceSpeechAfterAuthorization === true &&
        deviceSpeechInput?.kind === "dashboard";
      if (opts.playerKind === "brief-journey") {
        parkBriefJourneyAudioElement(player);
      } else if (!startDashboardDeviceSpeech) {
        clearDetachedAudioElement(player, requestObjectUrl);
      }
      const next = startDashboardDeviceSpeech
        ? dashboardDeviceSpeech.startDashboardPreparedAfterAuthorization()
        : dashboardDeviceSpeech.markAuthorized();
      if (!next) {
        throw new ApiError({
          status: 0,
          code: "device_speech_not_prepared",
          detail: audioCopy(lang)("audio.error.browserPlayback"),
        });
      }
      ownership.finish();
      completeDashboardAudioPlayAttempt(playAttemptId);
      return next;
    }
    if (resolution.kind === "selected-narrator-unavailable") {
      throw new ApiError({
        status: 422,
        code: "audio_voice_unsupported_for_language",
        detail: audioCopy(lang)("audio.error.narratorLanguage"),
      });
    }
    dashboardDeviceSpeech.discard();
    if (resolution.kind !== "mp3") {
      const invalidDeviceSpeech = resolution.kind === "invalid-device-speech";
      throw new ApiError({
        status: 0,
        code: invalidDeviceSpeech ? "device_speech_not_prepared" : "audio_no_content",
        detail: audioCopy(lang)(
          invalidDeviceSpeech ? "audio.error.browserPlayback" : "audio.error.noContent",
        ),
      });
    }
    opts.onResolved?.(data);
    const blob = decodeMp3(resolution.audioContent);
    requestObjectUrl = URL.createObjectURL(blob);
    objectUrl = requestObjectUrl;
    const downloadName = opts.downloadName || defaultDownloadName(opts.key, opts.label);
    player.loop = false;
    player.src = requestObjectUrl;
    audioEvents.armContent(requestObjectUrl);
    completion?.armContent(requestObjectUrl);
    player.load();
    player.playbackRate = playbackRate;
    await player.play();
    if (!ownership.isCurrent()) {
      clearDetachedAudioElement(player, requestObjectUrl, true);
      return getAudioPlaybackState();
    }
    setMediaSession(opts.label, opts.detail, lang);
    const next: AudioPlaybackState = {
      phase: "playing",
      key: opts.key,
      label: opts.label,
      detail: opts.detail,
      paused: false,
      buffering: false,
      positionMs: Math.round(Math.max(0, player.currentTime || 0) * 1000),
      durationMs: Math.round(finiteDuration(player) * 1000),
      buffered01: bufferedFraction(player),
      playbackRate,
      cache: data.cache,
      canDownload: true,
      downloadName,
    };
    setPlaybackState(next);
    completeDashboardAudioPlayAttempt(playAttemptId);
    recordAudioPlaybackStarted(next, opts.key);
    const retained = path === "/api/news-story-audio" || path === "/api/news-category-audio"
      ? retainDeliveredNewsAudio({ owner: retainedOwner, path, blob, label: opts.label, detail: opts.detail,
          language: lang, durationMs: next.durationMs || 0 })
      : retainDeliveredBriefAudio({ owner: retainedOwner, path, request: body, response: data, blob,
      label: opts.label, detail: opts.detail, language: lang, durationMs: next.durationMs || 0,
      fullBriefCompletion: opts.fullBriefCompletion === true,
    });
    completion?.bindRecording(retained);
    void retained.then((recording) => {
      if (recording && audioEl === player && retainedAudioOwnerIsCurrent(retainedOwner))
        bindRetainedAudioPauseCheckpoint({ owner: retainedOwner, recording, player, isPlayerCurrent: () => audioEl === player });
    }).catch(() => { /* A failed save cannot create a resume offer. */ });
    void retained.catch(() => { /* Playback remains available when browser storage is full or disabled. */ });
    ownership.finish();
    return next;
  } catch (err) {
    completion?.dispose();
    const isCurrent = ownership.isCurrent();
    clearDetachedAudioElement(player, requestObjectUrl, !isCurrent);
    if (!isCurrent) return getAudioPlaybackState();
    dashboardDeviceSpeech.discard();
    ownership.finish();
    const offer = captureDailyDefaultEditionAudioOffer(path, err, body, opts.key, opts.label, opts.detail);
    if (!offer) return emitAudioError(err, opts.key, opts.label, lang);
    return emitAudioError(err, opts.key, opts.label, lang, offer);
  }
}
export function downloadCurrentAudio(filename?: string): boolean {
  if (typeof document === "undefined" || !objectUrl || playbackState.phase !== "playing") {
    return false;
  }
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = sanitizeDownloadName(
    filename || playbackState.downloadName || playbackState.label || "",
  );
  link.rel = "noreferrer";
  document.body.appendChild(link);
  link.click();
  link.remove();
  trackPublicWebEvent("public_web_audio_download_clicked", {
    audio_kind: playbackState.key?.split(":")[0] || "audio",
  });
  return true;
}
export async function playQueuedAudio(entry: AudioQueueEntry): Promise<AudioPlaybackState> {
  const normalized = normalizeQueueEntry(entry);
  if (!normalized) {
    const copy = audioCopy();
    const next: AudioPlaybackState = {
      phase: "error",
      key: entry.key,
      label: entry.label,
      detail: copy("audio.error.queueUnavailable"),
      code: "audio_queue_entry_invalid",
    };
    setPlaybackState(next);
    return next;
  }
  if (!queuedAudioEntryIsCurrentMarketDay(normalized)) {
    removeAudioQueueEntry(normalized.id);
    const copy = audioCopy(stringValue(normalized.request.body.lang));
    const next: AudioPlaybackState = {
      phase: "error",
      key: normalized.key,
      label: normalized.label,
      detail: copy("audio.error.queueUnavailable"),
      code: "audio_queue_entry_expired",
    };
    setPlaybackState(next);
    return next;
  }
  const next = await postAudio(normalized.request.path, normalized.request.body, {
    key: normalized.key,
    label: normalized.label,
    detail: normalized.detail,
    fallbackCode: normalized.request.fallbackCode,
    activitySource: "user_initiated",
    lang: stringValue(normalized.request.body.lang),
    downloadName: normalized.request.downloadName,
  });
  if (next.phase !== "error") removeAudioQueueEntry(normalized.id);
  return next;
}
function articlePayload(item: AudioArticleInput) {
  return canonicalAudioArticle(item);
}
async function playResolvedBriefJourneyAudio(
  key: string,
  opts: BriefJourneySectionAudioOpts,
  data: AudioResponse,
): Promise<AudioPlaybackState> {
  clearDailyDefaultEditionAudioContext();
  const copy = audioCopy(opts.languageCode);
  const ownership = audioRequestOwnership.begin();
  const player = acquireBriefJourneyAudioElement();
  let requestObjectUrl: string | null = null;
  const playbackOpts = {
    key,
    label: opts.sectionTitle || copy("audio.label.briefSection"),
    detail: copy("audio.detail.briefNarration"),
  };
  const audioEvents = attachAudioEvents(player, {
    ...playbackOpts,
    lang: opts.languageCode,
    retainBriefJourneyPlayer: true,
  });
  setPlaybackState({ phase: "loading", ...playbackOpts });
  try {
    const resolution = resolveCanonicalAudioResponse(data, {
      requestedVoiceId: requestedAudioVoiceId(opts.voiceId),
    });
    if (resolution.kind !== "mp3") {
      const narratorUnavailable = resolution.kind === "selected-narrator-unavailable";
      throw new ApiError({
        status: narratorUnavailable ? 422 : 0,
        code: narratorUnavailable ? "audio_voice_unsupported_for_language" : "audio_no_content",
        detail: copy(
          narratorUnavailable ? "audio.error.narratorLanguage" : "audio.error.noContent",
        ),
      });
    }
    const blob = decodeMp3(resolution.audioContent);
    requestObjectUrl = URL.createObjectURL(blob);
    objectUrl = requestObjectUrl;
    player.loop = false;
    player.src = requestObjectUrl;
    audioEvents.armContent(requestObjectUrl);
    player.load();
    player.playbackRate = playbackRate;
    await player.play();
    if (!ownership.isCurrent()) {
      clearDetachedAudioElement(player, requestObjectUrl, true);
      return getAudioPlaybackState();
    }
    setMediaSession(playbackOpts.label, playbackOpts.detail, opts.languageCode);
    const next: AudioPlaybackState = {
      phase: "playing",
      ...playbackOpts,
      paused: false,
      buffering: false,
      positionMs: Math.round(Math.max(0, player.currentTime || 0) * 1000),
      durationMs: Math.round(finiteDuration(player) * 1000),
      buffered01: bufferedFraction(player),
      playbackRate,
      cache: data.cache || "memory",
      canDownload: true,
      downloadName: defaultDownloadName(key, playbackOpts.label),
    };
    setPlaybackState(next);
    recordAudioHistory({
      key,
      label: playbackOpts.label,
      detail: playbackOpts.detail,
      cache: next.cache,
    });
    trackPublicWebEvent("public_web_audio_play_started", {
      audio_kind: "brief-section",
      cache: next.cache || "memory",
    });
    ownership.finish();
    return next;
  } catch (error) {
    const isCurrent = ownership.isCurrent();
    clearDetachedAudioElement(player, requestObjectUrl, !isCurrent);
    if (!isCurrent) return getAudioPlaybackState();
    ownership.finish();
    return emitAudioError(error, key, playbackOpts.label, opts.languageCode);
  }
}
function playPreparedBriefJourneyDeviceSpeech(
  key: string,
  opts: BriefJourneySectionAudioOpts,
  speechText: string,
  startFromUserGesture = false,
): AudioPlaybackState {
  clearDailyDefaultEditionAudioContext();
  audioRequestOwnership.cancel();
  const copy = audioCopy(opts.languageCode);
  const label = opts.sectionTitle || copy("audio.label.briefSection");
  const detail = copy("audio.detail.briefNarration");
  const player = acquireBriefJourneyAudioElement();
  dashboardDeviceSpeech.discard();
  setPlaybackState({ phase: "loading", key, label, detail });
  const prepared = dashboardDeviceSpeech.prepare({
    body: briefJourneyRequestBody(opts),
    kind: "brief-journey",
    key,
    label,
    detail,
    lang: opts.languageCode,
    rate: playbackRate,
    speechText,
    hooks: deviceSpeechHooks(key, label, detail),
  });
  parkBriefJourneyAudioElement(player);
  const next = prepared
    ? startFromUserGesture
      ? dashboardDeviceSpeech.startBriefJourneyPreparedFromUserGesture()
      : dashboardDeviceSpeech.markAuthorized()
    : null;
  return (
    next ||
    emitAudioError(
      new ApiError({
        status: 0,
        code: "device_speech_not_prepared",
        detail: copy("audio.error.browserPlayback"),
      }),
      key,
      label,
      opts.languageCode,
    )
  );
}

function emitMissingPreparedBriefJourneyAudio(
  key: string,
  opts: BriefJourneySectionAudioOpts,
): AudioPlaybackState {
  const copy = audioCopy(opts.languageCode);
  return emitAudioError(
    new ApiError({
      status: 0,
      code: "brief_narration_not_prepared",
      detail: copy("brief.audio.notPrepared"),
    }),
    key,
    opts.sectionTitle || copy("audio.label.briefSection"),
    opts.languageCode,
  );
}

export async function playBriefJourneySectionAudio(
  opts: BriefJourneySectionAudioOpts,
  requirePrepared = false,
  activitySource: ActivitySource = "unknown",
): Promise<AudioPlaybackState> {
  const copy = audioCopy(opts.languageCode);
  const key = briefJourneySectionAudioKey(opts);
  const prepared = getPreparedBriefJourneyAudio(key);
  if (prepared?.kind === "mp3") return playResolvedBriefJourneyAudio(key, opts, prepared.response);
  if (prepared?.kind === "device-speech")
    return playPreparedBriefJourneyDeviceSpeech(key, opts, prepared.speechText);
  if (requirePrepared) return emitMissingPreparedBriefJourneyAudio(key, opts);
  return postAudio(
    `/api/brief/narration/item/${encodeURIComponent(opts.itemType)}/generate`,
    briefJourneyRequestBody(opts),
    {
      key,
      label: opts.sectionTitle || copy("audio.label.briefSection"),
      detail: copy("audio.detail.briefNarration"),
      fallbackCode: "brief_narration_unavailable",
      activitySource,
      lang: opts.languageCode,
      downloadName: defaultDownloadName(key, opts.sectionTitle),
      onResolved: (data) => rememberPreparedBriefJourneyMp3(key, data),
      playerKind: "brief-journey",
    },
  );
}

/** Keep non-async so browser activation reaches speech/media play. */
export function startBriefJourneySectionAudioFromUserGesture(
  opts: BriefJourneySectionAudioOpts,
): Promise<AudioPlaybackState> {
  const key = briefJourneySectionAudioKey(opts);
  const prepared = getPreparedBriefJourneyAudio(key);
  if (prepared?.kind === "mp3") {
    return playResolvedBriefJourneyAudio(key, opts, prepared.response);
  }
  if (prepared?.kind === "device-speech") {
    return Promise.resolve(
      playPreparedBriefJourneyDeviceSpeech(key, opts, prepared.speechText, true),
    );
  }
  return Promise.resolve(emitMissingPreparedBriefJourneyAudio(key, opts));
}

export type BriefingAudioOpts = {
  targetMinutes: 2 | 5;
  sourceId: string;
  categoryLabel: string;
  smartSummaryText: string;
  country?: string;
  lang?: string;
  userName?: string;
  timeOfDay?: string;
  localNewsCity?: string;
  localNewsOutlet?: string;
  articles?: AudioArticleInput[];
  audioSectionIds?: string[];
  voiceId?: string;
  facts?: Record<string, unknown>;
  /** Caller attests a prepared Premium full Brief; never sent in the API body. */
  fullBriefCompletion?: boolean;
  fullBriefContext?: BriefAudioCompletionContext;
  fullBriefStories?: AudioArticleInput[];
};

function dashboardBriefingPayload(opts: BriefingAudioOpts): Record<string, unknown> {
  return buildDashboardBriefingV2Payload({
    ...opts,
    voiceId: requestedAudioVoiceId(opts.voiceId),
  });
}

function dashboardBriefingLabel(opts: BriefingAudioOpts): string {
  return audioCopy(opts.lang)("audio.label.minutesBrief", {
    minutes: opts.targetMinutes === 5 ? 5 : 2,
  });
}
export function queueDashboardBriefing(opts: BriefingAudioOpts): AudioQueueEntry | null {
  const label = dashboardBriefingLabel(opts);
  const body = normalizeDashboardBriefingV2Payload(dashboardBriefingPayload(opts));
  if (!body) return null;
  return enqueueAudioRequest({
    key: dashboardBriefingAudioKey(),
    label,
    detail: opts.categoryLabel,
    path: "/api/dashboard-briefing-audio",
    body,
    fallbackCode: "briefing_failed",
    downloadName: defaultDownloadName(dashboardBriefingAudioKey(), label),
  });
}

export async function playDashboardBriefing(opts: BriefingAudioOpts): Promise<AudioPlaybackState> {
  const label = dashboardBriefingLabel(opts);
  const body = dashboardBriefingPayload(opts);
  return postAudio("/api/dashboard-briefing-audio", body, {
    key: dashboardBriefingAudioKey(),
    label,
    detail: opts.categoryLabel,
    fallbackCode: "briefing_failed",
    activitySource: "user_initiated",
    lang: opts.lang,
    downloadName: defaultDownloadName(dashboardBriefingAudioKey(), label),
    startDashboardDeviceSpeechAfterAuthorization: true,
    fullBriefCompletion: opts.fullBriefCompletion === true,
    fullBriefContext: opts.fullBriefContext,
    fullBriefStories: opts.fullBriefStories,
  });
}

export async function playNewsCategoryAudio(opts: {
  sourceId: string;
  categoryLabel: string;
  lang?: string;
  articles: AudioArticleInput[];
  localNewsCity?: string;
  localNewsOutlet?: string;
  homeIntelligenceBriefing?: string;
}): Promise<AudioPlaybackState> {
  const copy = audioCopy(opts.lang);
  const sourceId = stringValue(opts.sourceId) || "news";
  const categoryLabel = opts.categoryLabel || copy("audio.label.news");
  return postAudio(
    "/api/news-category-audio",
    {
      sourceId,
      categoryLabel: opts.categoryLabel || "News",
      lang: opts.lang || "en",
      articles: opts.articles.map(articlePayload),
      localNewsCity: opts.localNewsCity || "",
      localNewsOutlet: opts.localNewsOutlet || "",
      homeIntelligenceBriefing: stringValue(opts.homeIntelligenceBriefing),
    },
    {
      key: newsCategoryAudioKey(sourceId),
      label: copy("audio.label.categoryBriefing", { category: categoryLabel }),
      detail: copy("audio.detail.category"),
      fallbackCode: "news_category_audio_failed",
      activitySource: "user_initiated",
      lang: opts.lang,
    },
  );
}

export async function playNewsStoryAudio(
  item: AudioArticleInput,
  opts: { lang?: string } = {},
): Promise<AudioPlaybackState> {
  const copy = audioCopy(opts.lang);
  const payload = articlePayload(item);
  return postAudio(
    "/api/news-story-audio",
    {
      title: payload.title,
      description: payload.description,
      lang: opts.lang || "en",
    },
    {
      key: newsStoryAudioKey(item),
      label: payload.title || copy("audio.label.story"),
      detail: payload.publisherName || payload.sourceName || copy("audio.detail.newsStory"),
      fallbackCode: "news_story_audio_failed",
      activitySource: "user_initiated",
      lang: opts.lang,
    },
  );
}

export async function playSparkAudio(opts: {
  sparkId: string;
  title: string;
  lang?: string;
}): Promise<AudioPlaybackState> {
  const copy = audioCopy(opts.lang);
  return postAudio(
    "/api/spark/audio",
    {
      deviceId: getWebDeviceId(),
      sparkId: opts.sparkId,
      lang: opts.lang || "en",
    },
    {
      key: contentAudioKey("spark", opts.sparkId),
      label: opts.title || copy("audio.label.spark"),
      detail: copy("audio.label.spark"),
      fallbackCode: "spark_audio_failed",
      activitySource: "user_initiated",
      lang: opts.lang,
    },
  );
}

export async function playFactAudio(opts: {
  factId: string;
  title: string;
  factType?: string;
  lang?: string;
}): Promise<AudioPlaybackState> {
  const copy = audioCopy(opts.lang);
  return postAudio(
    "/api/facts/audio",
    {
      deviceId: getWebDeviceId(),
      factId: opts.factId,
      sparkId: opts.factId,
      factType: opts.factType || "fun",
      lang: opts.lang || "en",
    },
    {
      key: contentAudioKey("fact", opts.factId),
      label: opts.title || copy("audio.label.fact"),
      detail: copy("audio.label.fact"),
      fallbackCode: "fact_audio_failed",
      activitySource: "user_initiated",
      lang: opts.lang,
    },
  );
}

export async function playLearningAudio(opts: {
  lessonId: string;
  title: string;
  lang?: string;
  country?: string;
}): Promise<AudioPlaybackState> {
  const copy = audioCopy(opts.lang);
  return postAudio(
    "/api/learning/audio",
    {
      deviceId: getWebDeviceId(),
      sparkId: opts.lessonId,
      lessonId: opts.lessonId,
      title: opts.title,
      lang: opts.lang || "en",
      country: opts.country || "",
    },
    {
      key: contentAudioKey("lesson", opts.lessonId),
      label: opts.title || copy("audio.label.dailyMastery"),
      detail: copy("audio.label.dailyMastery"),
      fallbackCode: "learning_audio_failed",
      activitySource: "user_initiated",
      lang: opts.lang,
    },
  );
}

export async function playQuoteAudio(opts: {
  quoteId: string;
  text: string;
  author?: string | null;
  source?: string | null;
  lang?: string;
}): Promise<AudioPlaybackState> {
  const copy = audioCopy(opts.lang);
  return postAudio(
    "/api/quote/audio",
    {
      quoteId: opts.quoteId,
      text: opts.text,
      author: opts.author || "",
      source: opts.source || "",
      lang: opts.lang || "en",
    },
    {
      key: contentAudioKey("quote", opts.quoteId),
      label: opts.text || copy("audio.label.quoteOfDay"),
      detail: opts.author || copy("audio.detail.dailyReflection"),
      fallbackCode: "quote_audio_failed",
      activitySource: "user_initiated",
      lang: opts.lang,
    },
  );
}

export async function playTodayIdeaAudio(opts: {
  ideaId: string;
  title: string;
  audioScript: string;
  lang?: string;
}): Promise<AudioPlaybackState> {
  const copy = audioCopy(opts.lang);
  return postAudio(
    "/api/carousel-card-insight-audio",
    {
      cardId: opts.ideaId,
      audioScript: opts.audioScript,
      scriptOnly: true,
      lang: opts.lang || "en",
    },
    {
      key: contentAudioKey("idea", opts.ideaId),
      label: opts.title || copy("audio.label.todayIdea"),
      detail: copy("audio.label.todayIdea"),
      fallbackCode: "today_idea_audio_failed",
      activitySource: "user_initiated",
      lang: opts.lang,
    },
  );
}

export async function playStockWhyAudio(opts: {
  symbol: string;
  displayName: string;
  direction: "up" | "down";
  lang?: string;
  changePercent?: number | null;
  price?: number | null;
  currency?: string | null;
  country?: string;
  primaryBenchmarkChangePercent?: number | null;
  secondaryBenchmarkChangePercent?: number | null;
  recentNewsSummary?: string;
}): Promise<AudioPlaybackState> {
  const copy = audioCopy(opts.lang);
  const symbol = String(opts.symbol || "")
    .trim()
    .toUpperCase();
  return postAudio(
    "/api/stock-why-audio",
    {
      symbol,
      displayName: opts.displayName,
      direction: opts.direction,
      lang: opts.lang || "en",
      changePercent: opts.changePercent,
      price: opts.price,
      currency: opts.currency || "",
      country: opts.country || "",
      primaryBenchmarkChangePercent: opts.primaryBenchmarkChangePercent,
      secondaryBenchmarkChangePercent: opts.secondaryBenchmarkChangePercent,
      recentNewsSummary: opts.recentNewsSummary || "",
    },
    {
      key: stockWhyAudioKey(symbol),
      label: copy("audio.label.stockMove", {
        symbol: symbol || copy("audio.label.stock"),
      }),
      detail: opts.displayName,
      fallbackCode: "stock_why_audio_failed",
      activitySource: "user_initiated",
      lang: opts.lang,
    },
  );
}
